import { useState, useEffect, useRef } from "react";
import {
  getAllRefundData,
  searchPaymentException,
  initiateRefund,
  getOrganisation,
  getProvidersNames,
} from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import Spinner from "../../Spin/Spinner";
import { FaFilter } from "react-icons/fa";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/common/ConfirmModal";
import PaymentDrawer from "./PaymentDrawer";
import DateFilter from "../../../components/common/DateFilter";

export default function PaymentExceptionDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchType, setSearchType] = useState("transactionId");
  const [searchValue, setSearchValue] = useState("");
  const token = useSelector((state) => state.userReducer.token);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [orgFilter, setOrgFilter] = useState("");
  const [pgFilter, setPgFilter] = useState("");

  const [orgOptions, setOrgOptions] = useState([]);
  const [pgOptions, setPgOptions] = useState([]);

  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showPgDropdown, setShowPgDropdown] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const orgRef = useRef();
  const pgRef = useRef();
  const [showActivityDrawer, setShowActivityDrawer] = useState(false);
  const [selectedPaymentExceptionId, setSelectedPaymentExceptionId] =
    useState(null);
  const [impsLoadingId, setImpsLoadingId] = useState(null);
  const [accountNumber, setAccountNumber] = useState("033325221933414");
  const [ifscCode, setIfscCode] = useState("hdfc0041355");
  const [impsAmount, setImpsAmount] = useState("400");
  const [impsResponse, setImpsResponse] = useState("");
  const [impsBeneName, setimpsBeneName] = useState("");
  const [impsRemark, setImpsRemark] = useState("");
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await getAllRefundData(token, {
        page,
        perPage,
        org: orgFilter || undefined,
        pg: pgFilter || undefined,
        fromDate: startDate || undefined,
        toDate: endDate || undefined,
      });

      const formatted = res.data.map((item) => ({
        ...item,
        amount: Number(item.amount) || 0,
      }));

      setData(formatted);
      setError(null);
      setTotalPages(res.meta.totalPages);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
    fetchProviders();
  }, []);

  const fetchOrganisations = async () => {
    try {
      const response = await getOrganisation(token);
      const orgs = response?.data || [];
      setOrgOptions(orgs.map((item) => item.orgName).filter(Boolean));
    } catch (err) {
      console.log(err);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await getProvidersNames(token);
      const providerData = response || [];
      const uniqueProviders = [
        ...new Map(
          providerData.map((item) => [
            item.label,
            {
              label: item.label,
            },
          ]),
        ).values(),
      ];

      setPgOptions(uniqueProviders.map((item) => item.label).filter(Boolean));
    } catch (err) {
      console.log(err);
    }
  };

  // reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [orgFilter, pgFilter, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [page, orgFilter, pgFilter, startDate, endDate]);

  const handleSearch = async () => {
    if (!searchValue) return;

    try {
      setLoading(true);
      const res = await searchPaymentException(token, searchType, searchValue);

      if (res.success) {
        const formatted = res.data.map((item) => ({
          ...item,
          amount: Number(item.amount) || 0,
        }));
        setData(formatted);
        setError(null);
      } else {
        setData([]);
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setSearchValue("");
    setError(null);

    setOrgFilter("");
    setPgFilter("");

    const today = new Date();
    setStartDate(today);
    setEndDate(today);

    setPage(1);

    fetchData();
  };

  const handleDateChange = (fromDate, toDate) => {
    setStartDate(fromDate);
    setEndDate(toDate);
  };

  const handleOpenActivity = (id) => {
    setSelectedPaymentExceptionId(id);
    setShowActivityDrawer(true);
  };

  const openConfirmation = () => {
    return new Promise((resolve) => {
      setIsConfirmModal(() => ({
        isOpen: true,
        onResolve: resolve,
      }));
    });
  };

  const handleImpsSubmit = async (row) => {
    if (!accountNumber || !ifscCode || !impsAmount) {
      toast.error("Account Number, IFSC and Amount are required");
      return;
    }

    const userConfirmed = await openConfirmation();
    if (!userConfirmed) return;

    setImpsLoadingId(row.id);

    try {
      // STEP 1 : Payout API
      const payoutPayload = {
        accountType: "savings",
        amount: impsAmount,
        mode: "IMPS",
        ifscCode,
        accountNo: accountNumber,
        branchname: "SBI",
        beneName: impsBeneName || "Testing",
        beneEmail: "test@gmail.com",
        beneMobile: "+919876546789",
        beneBankName: "SBI BANK",
        remark: impsRemark || "Refund Test",
      };

      const payoutRes = await fetch(
        "https://admin.kwicpe.com/api/v1/payout/initiate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "api-key":
              "c672e496899f61488e90.aLHGzBYG9hWMuu39ZnQNwIJsliUtqCBwv2Si",
          },
          body: JSON.stringify(payoutPayload),
        },
      );

      const payoutData = await payoutRes.json();
      // console.log("first", payoutData);

      // STEP 2 : Create RefundAttempt + Update PaymentException
      // console.log("firstdfa",row)
      await initiateRefund(token, {
        paymentExceptionId: row.id,
        referenceId: row.referenceId,
        transactionId: row.transactionId,

        // Payout data
        requestOrderId: payoutData.ORDER_ID,
        payoutReferenceId: payoutData.MERCHANT_REF_NO,
        responseMessage: payoutData.RESPONSE_MESSAGE,

        accountNo: accountNumber,
        ifsc: ifscCode,
        amount: Number(impsAmount),
        mode: "IMPS",

        beneName: impsBeneName || "Testing",
        beneMobile: "+919876546789",
        beneEmail: "test@gmail.com",
        beneBankName: "SBI BANK",

        status:
          payoutData.RESPONSE_STATUS === "SETTLEMENT_PROCESS"
            ? "PENDING"
            : "FAILED",
      });

      // STEP 3 : Update UI immediately

      setData((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
                ...item,
                refundStatus: "PENDING",
                refundAttempts: (item.refundAttempts || 0) + 1,
                lastRefundAttemptAt: new Date().toISOString(),
              }
            : item,
        ),
      );

      toast.success("Refund Initiated Successfully");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Refund Failed");
    } finally {
      setImpsLoadingId(null);
    }
  };

  return (
    <>
      <div style={styles.container}>
        <h2 style={styles.title}>⚠ Payment Exceptions</h2>

        {loading && <Spinner />}
        <div style={styles.filterContainer}>
          {/* Search */}
          <div style={styles.searchBar}>
            <select
              style={styles.input}
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="transactionId">Transaction ID</option>
              <option value="referenceId">Reference ID</option>
              <option value="utr">UTR</option>
            </select>

            <input
              style={styles.input}
              type="text"
              placeholder="Enter value..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />

            <button
              style={{ ...styles.button, ...styles.primaryBtn }}
              onClick={handleSearch}
            >
              Search
            </button>

            <button
              style={{ ...styles.button, ...styles.secondaryBtn }}
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
        {/* Calender */}
        <DateFilter onDateChange={handleDateChange} />

        {/* Table */}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {/* ORG HEADER */}
                <th style={styles.th}>
                  <div style={styles.headerWithIcon} ref={orgRef}>
                    Org Name
                    <FaFilter
                      style={styles.filterIcon}
                      onClick={() => setShowOrgDropdown((p) => !p)}
                    />
                    {showOrgDropdown && (
                      <div style={styles.dropdown}>
                        <div
                          style={styles.dropdownItem}
                          onClick={() => {
                            setOrgFilter("");
                            setShowOrgDropdown(false);
                          }}
                        >
                          All
                        </div>
                        {orgOptions.map((org) => (
                          <div
                            key={org}
                            style={styles.dropdownItem}
                            onClick={() => {
                              setOrgFilter(org);
                              setShowOrgDropdown(false);
                            }}
                          >
                            {org}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </th>

                {/* PG HEADER */}
                <th style={styles.th}>
                  <div style={styles.headerWithIcon} ref={pgRef}>
                    PG Name
                    <FaFilter
                      style={styles.filterIcon}
                      onClick={() => setShowPgDropdown((p) => !p)}
                    />
                    {showPgDropdown && (
                      <div style={styles.dropdown}>
                        <div
                          style={styles.dropdownItem}
                          onClick={() => {
                            setPgFilter("");
                            setShowPgDropdown(false);
                          }}
                        >
                          All
                        </div>
                        {pgOptions.map((pg) => (
                          <div
                            key={pg}
                            style={styles.dropdownItem}
                            onClick={() => {
                              setPgFilter(pg);
                              setShowPgDropdown(false);
                            }}
                          >
                            {pg}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </th>
                <th style={styles.th}>Reference Id</th>
                <th style={styles.th}>Transaction Id</th>
                <th style={styles.th}>UTR</th>
                <th style={styles.th}>Amt</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Refund Status</th>
                <th style={styles.th}>Attempts</th>
                <th style={styles.th}>Last Attempt</th>
                <th style={styles.th}>Activity</th>
                <th style={styles.th}>Action</th>
                {/* <th>Action</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="13"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan="12"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#dc2626",
                      fontWeight: "500",
                    }}
                  >
                    {error}
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan="12"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr
                    key={row.id}
                    style={styles.tableRow}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#ffffff")
                    }
                  >
                    <td style={styles.td}>{row.orgName}</td>
                    <td style={styles.td}>{row.pgName}</td>
                    <td style={styles.td}>{row.referenceId}</td>
                    <td style={styles.td}>{row.transactionId}</td>
                    <td style={styles.td}>{row.utr}</td>
                    <td style={styles.td}>₹{row.amount}</td>
                    <td style={styles.td}>
                      {new Date(row.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>

                    <td style={styles.td}>
                      <span style={styles.badge(row.status.toLowerCase())}>
                        {row.status}
                      </span>
                    </td>

                    <td style={styles.td}>
                      <span
                        style={styles.badge(row.refundStatus.toLowerCase())}
                      >
                        {row.refundAttempts === 0
                          ? "Not Initiated"
                          : row.refundStatus}
                      </span>
                    </td>

                    <td style={styles.td}>{row.refundAttempts}</td>

                    <td style={styles.td}>
                      {row.lastRefundAttemptAt
                        ? new Date(row.lastRefundAttemptAt).toLocaleString(
                            "en-IN",
                          )
                        : "-"}
                    </td>

                    <td style={styles.td}>
                      <button
                        style={styles.showBtn}
                        onClick={() => handleOpenActivity(row.id)}
                      >
                        Show
                      </button>
                    </td>

                    <td style={styles.td}>
                      {" "}
                      <button
                        style={{
                          ...styles.actionBtn,
                          opacity:
                            row.refundStatus === "SETTLED" ||
                            row.refundStatus === "SUCCESS" ||
                            row.refundStatus === "SETTLEMENT_PROCESS" ||
                            (row.refundStatus === "PENDING" &&
                              row.refundAttempts > 0)
                              ? 0.5
                              : 1,
                          cursor:
                            row.refundStatus === "SETTLED" ||
                            row.refundStatus === "SUCCESS" ||
                            row.refundStatus === "SETTLEMENT_PROCESS" ||
                            (row.refundStatus === "PENDING" &&
                              row.refundAttempts > 0)
                              ? "not-allowed"
                              : "pointer",
                        }}
                        onClick={() => handleImpsSubmit(row)}
                        disabled={
                          impsLoadingId === row.id ||
                          ["SETTLED", "SUCCESS", "SETTLEMENT_PROCESS"].includes(
                            row.refundStatus,
                          ) ||
                          (row.refundStatus === "PENDING" &&
                            row.refundAttempts > 0)
                        }
                      >
                        {" "}
                        {impsLoadingId === row.id ? (
                          <div className="button-loader"></div>
                        ) : (
                          "Attempt"
                        )}{" "}
                      </button>{" "}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={styles.pagination}>
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
          >
            Prev
          </button>

          <span style={styles.pageText}>
            Page {page} / {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={
              page === totalPages ? styles.pageBtnDisabled : styles.pageBtn
            }
          >
            Next
          </button>
        </div>
      </div>
      {isConfirmModal && (
        <ConfirmationModal
          onResolve={(decision) => {
            isConfirmModal.onResolve(decision);
            setIsConfirmModal(false);
          }}
        />
      )}
      {showActivityDrawer && (
        <PaymentDrawer
          paymentExceptionId={selectedPaymentExceptionId}
          onClose={() => setShowActivityDrawer(false)}
        />
      )}
    </>
  );
}

// ---------- STYLES ----------
const styles = {
  container: {
    padding: "20px",
    fontFamily: "Inter, sans-serif",
    background: "#f9fafb",
  },
  title: {
    fontSize: "22px",
    fontWeight: "600",
    marginBottom: "16px",
  },
  filterContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    gap: "20px",
    flexWrap: "wrap",
  },
  searchBar: {
    display: "flex",
    gap: "10px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  input: {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
  },
  button: {
    padding: "8px 14px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
  },
  primaryBtn: {
    background: "#fc8019",
    color: "#fff",
  },
  secondaryBtn: {
    background: "#e5e7eb",
  },
  tableWrapper: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    overflowX: "auto",
    overflowY: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: "1500px",
    fontSize: "13px",
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f8fafc",
    color: "#475569",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    // zIndex: 5,
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
    color: "#1e293b",
    fontSize: "13px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  tableRow: {
    transition: "all 0.2s ease",
    cursor: "default",
  },
  tableRowHover: {
    background: "#f8fafc",
  },
  badge: (status) => {
    const s = status?.toLowerCase();

    const config = {
      success: {
        bg: "#dcfce7",
        text: "#15803d",
      },
      completed: {
        bg: "#dcfce7",
        text: "#15803d",
      },
      failed: {
        bg: "#fee2e2",
        text: "#dc2626",
      },
      pending: {
        bg: "#fef3c7",
        text: "#d97706",
      },
      processing: {
        bg: "#dbeafe",
        text: "#2563eb",
      },
    };
    const current = config[s] || {
      bg: "#f1f5f9",
      text: "#475569",
    };
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "5px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      background: current.bg,
      color: current.text,
      border: `1px solid ${current.bg}`,
      minWidth: "80px",
    };
  },
  actionBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "8px",
    background: "#fc8019",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  headerWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    position: "relative",
  },

  filterIcon: {
    fontSize: "11px",
    cursor: "pointer",
    color: "#64748b",
    transition: "all 0.2s ease",
  },

  dropdown: {
    position: "absolute",
    top: "34px",
    left: 0,
    minWidth: "180px",
    maxHeight: "250px",
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
    zIndex: 1000,
  },

  dropdownItem: {
    padding: "10px 14px",
    fontSize: "13px",
    color: "#334155",
    cursor: "pointer",
    borderBottom: "1px solid #f8fafc",
  },

  center: { textAlign: "center", padding: 20 },

  pagination: {
    padding: "16px 0",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "12px",
  },

  pageBtn: {
    padding: "6px 12px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    background: "#fc8019",
    cursor: "pointer",
    color: "#fff",
  },

  pageBtnDisabled: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#94a3b8",
    cursor: "not-allowed",
  },

  pageText: {
    fontSize: "13px",
    color: "#475569",
    fontWeight: "600",
  },

  showBtn: {
    padding: "7px 14px",
    border: "1px solid #2563eb",
    background: "#eff6ff",
    color: "#2563eb",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
  },
};
